import { useEffect, useState } from "react";
import { deriveBrowseOnly } from "@/lib/gate";
import type { UserProfile } from "@/types/mentors";

export function useUser() {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let unsubAuth: (() => void) | null = null;
    let unsubDoc: (() => void) | null = null;

    void Promise.all([import("firebase/auth"), import("firebase/firestore")])
      .then(([authModule, firestoreModule]) => {
        if (cancelled) return;

        const auth = authModule.getAuth();
        const db = firestoreModule.getFirestore();

        unsubAuth = authModule.onAuthStateChanged(auth, (u) => {
          try {
            unsubDoc?.();
          } catch {
            // ignore cleanup failures
          }
          unsubDoc = null;

          if (!u) {
            setUser(null);
            setLoading(false);
            return;
          }

          unsubDoc = firestoreModule.onSnapshot(firestoreModule.doc(db, "users", u.uid), (snap) => {
            const d = (snap.data() || {}) as any;
            const roles = d.roles || [];
            const planId = (d.planId || "goalbuddy_free") as any;
            const browseOnly = deriveBrowseOnly(planId, roles);
            setUser({
              uid: u.uid,
              displayName: d.displayName || u.displayName || "",
              photoURL: d.photoURL || u.photoURL || "",
              roles,
              planId,
              browseOnly,
            });
            setLoading(false);
          });
        });
      })
      .catch(() => {
        if (cancelled) return;
        setUser(null);
        setLoading(false);
      });

    return () => {
      cancelled = true;
      try {
        unsubDoc?.();
      } catch {
        // ignore cleanup failures
      }
      try {
        unsubAuth?.();
      } catch {
        // ignore cleanup failures
      }
    };
  }, []);

  return { user, loading };
}
